import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  CalendarDays,
  CheckCircle2,
  HelpCircle,
  LayoutGrid,
  Lightbulb,
  ListChecks,
  MinusCircle,
  ScrollText,
  Table2,
  XCircle,
} from 'lucide-react';
import './tender-analysis.css';

/**
 * Tender Analysis results view — redesign (AI2-546).
 *
 * Ported from the Claude Design prototype (QSAI Redesign v2): a left rail
 * of report sections plus an overview landing screen with KPI cards and
 * clickable section cards. Renders the 7-section analysis_results payload:
 *
 *   executive_summary        { key_findings[], price_comparison[],
 *                              programme_comparison[], quote_quality_assessment[],
 *                              recommendations[] }
 *   price_analysis           { total_prices[], preliminaries[], other_fees[],
 *                              provisional_sums[] }
 *   programme_analysis       { lead_in_periods[], overall_duration[], site_visits[] }
 *   inclusions_exclusions    { scope_differences[], commercial_terms[] }
 *   risks                    { risks[] }
 *   opportunities            { opportunities[] }
 *   clarifications_required  { clarifications_required[] }
 *
 * Sections stream in while the analysis runs: missing sections show a
 * spinner in the rail and a pending card in the content column.
 */

const SUB_TONES = ['success', 'info', 'warning', 'danger', 'neutral'];

const asArray = (value) => (Array.isArray(value) ? value : []);

// Aggressive normalisation: the model quotes company names from the
// documents, which rarely match the upload names exactly ("C.P.S.
// Cleaning" vs "CPS Cleaning", "In 2 Clean" vs "in2clean").
const normalizeName = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

// Chips labelled "Both"/"All" refer to every bidder, not a bidder — they
// must not consume (or wrap into) a subcontractor colour.
const isCollectiveName = (normalized) =>
  ['both', 'all', 'general', 'multiple'].includes(normalized);

// 'N/A' marks client-directed entries (e.g. BoQ-gap clarifications) — like
// collective names it must never register as a bidder or take a colour slot.
const isNonBidderName = (normalized) =>
  isCollectiveName(normalized) || ['na', 'notapplicable'].includes(normalized);

// Two normalised names refer to the same bidder when equal or when one
// contains the other ("cps" ⊂ "cpscleaning", "paragoncleaning" ⊂
// "paragoncleaningcoltd").
const sameEntity = (a, b) =>
  a === b ||
  (a.length >= 3 && b.includes(a)) ||
  (b.length >= 3 && a.includes(b));

// Payload keys in pipeline-completion order: price_analysis always
// finishes first (phase 1), so collecting names in this order keeps a
// bidder's colour slot stable while the remaining sections stream in.
const NAME_COLLECTION_ORDER = [
  'price_analysis',
  'programme_analysis',
  'inclusions_exclusions',
  'risks',
  'opportunities',
  'clarifications_required',
  'executive_summary',
];

const SECTIONS = [
  {
    key: 'executive_summary',
    title: 'Executive Summary',
    Icon: ScrollText,
    count: (d) => asArray(d?.key_findings).length,
    summary: 'Key findings, submission quality and recommended actions',
  },
  {
    key: 'price_analysis',
    title: 'Price Analysis',
    Icon: Banknote,
    count: (d) =>
      asArray(d?.total_prices).length +
      asArray(d?.other_fees).length +
      asArray(d?.provisional_sums).length,
    summary: 'Totals, fees and preliminaries as submitted',
  },
  {
    key: 'programme_analysis',
    title: 'Programme',
    Icon: CalendarDays,
    count: (d) =>
      Math.max(
        asArray(d?.overall_duration).length,
        asArray(d?.lead_in_periods).length
      ),
    summary: 'Lead-in, duration on site and resource levels compared',
  },
  {
    key: 'inclusions_exclusions',
    title: 'Inclusions & Exclusions',
    Icon: ListChecks,
    count: (d) =>
      asArray(d?.scope_differences).length +
      asArray(d?.commercial_terms).length,
    summary: 'Scope items and commercial terms compared side by side',
  },
  {
    key: 'risks',
    title: 'Risks',
    Icon: AlertTriangle,
    count: (d) => asArray(d?.risks).length,
    summary: 'Risks across pricing, technical, commercial and programme',
  },
  {
    key: 'opportunities',
    title: 'Opportunities',
    Icon: Lightbulb,
    count: (d) => asArray(d?.opportunities).length,
    summary: 'Levers for savings, certainty or negotiation leverage',
  },
  {
    key: 'clarifications_required',
    title: 'Clarifications',
    Icon: HelpCircle,
    count: (d) => asArray(d?.clarifications_required).length,
    summary: 'A ready-made RFI list to issue before any award',
  },
];

// ---- shared bits -------------------------------------------------------------

// AI2-591: which pricing basis produced this report. A BoQ swaps the entire
// prompt set (templates/tender_analysis_boq/), so two runs of the same package
// can differ structurally — the quotes-only state is rendered too, otherwise an
// absent notice is ambiguous between "no BoQ" and "run before this existed".
const PricingBasis = ({ boqFileName }) => {
  const withBoq = Boolean(boqFileName);
  return (
    <div className={`ta-basis ${withBoq ? 'with-boq' : ''}`}>
      <Table2 size={16} className="ta-basis-icon" />
      <span className="ta-basis-label">Pricing basis</span>
      <span className="ta-basis-value">
        {withBoq ? (
          <>
            Bill of Quantities
            <span className="ta-basis-file" title={boqFileName}>
              {boqFileName}
            </span>
          </>
        ) : (
          'Quotes only'
        )}
      </span>
    </div>
  );
};

PricingBasis.propTypes = {
  boqFileName: PropTypes.string,
};

const SubChip = ({ name, toneFor }) => (
  <span className={`ta-chip tone-${toneFor(name)}`}>{name}</span>
);

SubChip.propTypes = {
  name: PropTypes.string.isRequired,
  toneFor: PropTypes.func.isRequired,
};

const FilterChips = ({ options, active, onChange }) => (
  <div className="ta-filters">
    {options.map((option) => (
      <button
        key={option}
        type="button"
        className={`ta-filter ${active === option ? 'active' : ''}`}
        onClick={() => onChange(option)}
      >
        {option}
      </button>
    ))}
  </div>
);

FilterChips.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SectionHead = ({ title, subtitle, children }) => (
  <div className={`ta-section-head ${children ? 'with-filters' : ''}`}>
    <div>
      <h2>{title}</h2>
      {subtitle && <div className="ta-section-sub">{subtitle}</div>}
    </div>
    {children}
  </div>
);

SectionHead.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node,
};

// Columns carry an explicit `width` because the model returns free text of
// wildly varying length in any field — a paragraph in one cell must not
// starve its neighbours down to one word per line. `table-layout: fixed`
// (see .ta-table) honours these and ignores content width.
const DataTable = ({ columns, rows }) => (
  <div style={{ overflowX: 'auto' }}>
    <table className="ta-table">
      <colgroup>
        {columns.map((col) => (
          <col key={col.key} style={{ width: col.width }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {columns.map((col) => (
              <td key={col.key} className={col.strong ? 'strong' : ''}>
                {col.render ? col.render(row) : formatCell(row[col.key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const formatCell = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// ---- overview ----------------------------------------------------------------

const Overview = ({ tenderData, counts, inProgress, toneFor, onNavigate }) => {
  const totals = asArray(tenderData?.price_analysis?.total_prices);
  const findings = asArray(tenderData?.executive_summary?.key_findings);

  return (
    <>
      {totals.length > 0 && (
        <div className="ta-kpis">
          {totals.map((t, i) => (
            <div className="ta-kpi" key={i}>
              <div>
                <SubChip name={String(t.subcontractor)} toneFor={toneFor} />
              </div>
              <div className="ta-kpi-value">{formatCell(t.base_price)}</div>
              <div className="ta-kpi-sub">{formatCell(t.price_includes)}</div>
            </div>
          ))}
        </div>
      )}

      {findings.length > 0 && (
        <div className="ta-card">
          <div className="ta-card-title">Top findings</div>
          <div className="ta-findings">
            {findings.slice(0, 3).map((finding, i) => (
              <div className="ta-finding" key={i}>
                <span className="ta-finding-dot" />
                <span>{formatCell(finding)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="ta-overview-heading">Dig into the analysis</div>
        <div className="ta-section-cards">
          {SECTIONS.map(({ key, title, Icon, summary }) => {
            const pending = !tenderData?.[key] && inProgress;
            return (
              <button
                key={key}
                type="button"
                className={`ta-section-card ${pending ? 'pending' : ''}`}
                onClick={() => onNavigate(key)}
              >
                <div className="ta-section-card-head">
                  <Icon size={20} className="ta-sc-icon" />
                  <span className="ta-sc-title">{title}</span>
                  {pending ? (
                    <CircularProgress size={14} thickness={5} />
                  ) : (
                    <ArrowRight size={18} className="ta-sc-arrow" />
                  )}
                </div>
                <div className="ta-section-card-meta">
                  <span>
                    {pending
                      ? 'Processing…'
                      : counts[key] > 0
                        ? `${counts[key]} items`
                        : 'View section'}
                  </span>
                </div>
                <div className="ta-section-card-summary">{summary}</div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

Overview.propTypes = {
  tenderData: PropTypes.object,
  counts: PropTypes.object.isRequired,
  inProgress: PropTypes.bool.isRequired,
  toneFor: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

// ---- executive summary ---------------------------------------------------------

const ExecutiveSummary = ({ data, taxTerm, toneFor }) => {
  const findings = asArray(data.key_findings);
  const quality = asArray(data.quote_quality_assessment);
  const recommendations = asArray(data.recommendations);
  const priceComparison = asArray(data.price_comparison);
  const programmeComparison = asArray(data.programme_comparison);

  return (
    <>
      <SectionHead
        title="Executive Summary"
        subtitle="Key findings, submission quality and recommended actions"
      />

      {findings.length > 0 && (
        <div className="ta-card">
          <div className="ta-card-title">Key findings</div>
          <div className="ta-findings">
            {findings.map((finding, i) => (
              <div className="ta-finding" key={i}>
                <span className="ta-finding-dot" />
                <span>{formatCell(finding)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {priceComparison.length > 0 && (
        <div className="ta-card">
          <div className="ta-card-title">Price comparison</div>
          <DataTable
            columns={[
              {
                key: 'subcontractor',
                label: 'Subcontractor',
                width: '18%',
                render: (row) => (
                  <SubChip name={String(row.subcontractor)} toneFor={toneFor} />
                ),
              },
              {
                key: 'total_price',
                label: 'Total price',
                width: '16%',
                strong: true,
              },
              {
                key: 'tax_included',
                label: `${taxTerm} included`,
                width: '16%',
              },
              { key: 'price_includes', label: 'Price includes', width: '50%' },
            ]}
            rows={priceComparison}
          />
        </div>
      )}

      {programmeComparison.length > 0 && (
        <div className="ta-card">
          <div className="ta-card-title">Programme comparison</div>
          <DataTable
            columns={[
              {
                key: 'subcontractor',
                label: 'Subcontractor',
                width: '18%',
                render: (row) => (
                  <SubChip name={String(row.subcontractor)} toneFor={toneFor} />
                ),
              },
              { key: 'lead_in_period', label: 'Lead-in', width: '16%' },
              {
                key: 'total_weeks',
                label: 'Total weeks',
                // 15% floor: the prompt asks for "Number or 'Not specified'"
                // but the same instruction on number_of_visits still returned
                // sentences, and under table-layout: fixed this column cannot
                // grow. Too narrow and free text shreds vertically here
                // instead of in the column this PR widened.
                width: '15%',
                strong: true,
              },
              { key: 'constraints', label: 'Constraints', width: '51%' },
            ]}
            rows={programmeComparison}
          />
        </div>
      )}

      {quality.length > 0 && (
        <div className="ta-grid-2">
          {quality.map((q, i) => (
            <div className="ta-card" key={i}>
              <div className="ta-quality-head">
                <SubChip name={String(q.subcontractor)} toneFor={toneFor} />
                <span className="spacer" />
                <span className="ta-quality-label">Quality</span>
                <span className={`ta-chip tone-${qualityTone(q.quality)}`}>
                  {formatCell(q.quality)}
                </span>
              </div>
              <div className="ta-sw-title strengths">Strengths</div>
              <div className="ta-sw-list strengths">
                {asArray(q.strengths).map((s, j) => (
                  <div className="ta-sw-item strength" key={j}>
                    <CheckCircle2 size={15} />
                    <span>{formatCell(s)}</span>
                  </div>
                ))}
              </div>
              <div className="ta-sw-title weaknesses">Weaknesses</div>
              <div className="ta-sw-list">
                {asArray(q.weaknesses).map((w, j) => (
                  <div className="ta-sw-item weakness" key={j}>
                    <XCircle size={15} />
                    <span>{formatCell(w)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="ta-card">
          <div className="ta-card-title">Recommended actions</div>
          <div className="ta-recs">
            {recommendations.map((rec, i) => (
              <div className="ta-rec" key={i}>
                <span className="ta-rec-n">{i + 1}</span>
                <span className="ta-rec-text">{formatCell(rec)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

ExecutiveSummary.propTypes = {
  data: PropTypes.object.isRequired,
  taxTerm: PropTypes.string.isRequired,
  toneFor: PropTypes.func.isRequired,
};

const qualityTone = (quality) => {
  const q = normalizeName(quality);
  if (q.startsWith('high') || q.startsWith('good')) return 'success';
  if (q.startsWith('low') || q.startsWith('poor')) return 'danger';
  return 'warning';
};

// ---- price analysis ------------------------------------------------------------

const FeeRows = ({ title, subtitle, rows, toneFor }) => (
  <div className="ta-card">
    <div className="ta-card-title">
      {title}
      {subtitle && <span className="ta-card-subtitle">{subtitle}</span>}
    </div>
    <div className="ta-fee-rows">
      {rows.map((fee, i) => (
        <div className="ta-fee-row" key={i}>
          <SubChip name={String(fee.subcontractor)} toneFor={toneFor} />
          <span className="ta-fee-desc">{formatCell(fee.description)}</span>
          <span className="ta-fee-amount">{formatCell(fee.amount)}</span>
          <span className="ta-fee-note">{formatCell(fee.notes)}</span>
        </div>
      ))}
    </div>
  </div>
);

FeeRows.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  toneFor: PropTypes.func.isRequired,
};

const PriceAnalysis = ({ data, taxTerm, toneFor }) => {
  const totals = asArray(data.total_prices);
  const preliminaries = asArray(data.preliminaries);
  const otherFees = asArray(data.other_fees);
  const provisionalSums = asArray(data.provisional_sums);

  return (
    <>
      <SectionHead
        title="Price Analysis"
        subtitle="Totals, fees and preliminaries as submitted"
      />

      {totals.length > 0 && (
        <div className="ta-grid-2">
          {totals.map((t, i) => (
            <div className="ta-card ta-total-card" key={i}>
              <SubChip name={String(t.subcontractor)} toneFor={toneFor} />
              <div className="ta-total-amount">{formatCell(t.base_price)}</div>
              <div className="ta-total-label">Base total (excl. {taxTerm})</div>
              <div className="ta-total-stats">
                <div>
                  <div className="ta-stat-label">{taxTerm}</div>
                  <div className="ta-stat-value">
                    {formatCell(t.tax_amount)}
                  </div>
                </div>
                <div>
                  <div className="ta-stat-label">Incl. {taxTerm}</div>
                  <div className="ta-stat-value">
                    {formatCell(t.total_inc_tax)}
                  </div>
                </div>
              </div>
              <div className="ta-total-includes">
                {formatCell(t.price_includes)}
              </div>
            </div>
          ))}
        </div>
      )}

      {otherFees.length > 0 && (
        <FeeRows
          title="Other fees"
          subtitle="Priced items that sit outside a directly comparable basis"
          rows={otherFees}
          toneFor={toneFor}
        />
      )}

      {provisionalSums.length > 0 && (
        <FeeRows
          title="Provisional sums"
          rows={provisionalSums}
          toneFor={toneFor}
        />
      )}

      {preliminaries.length > 0 && (
        <div className="ta-grid-2">
          {preliminaries.map((p, i) => (
            <div className="ta-card" key={i}>
              <div className="ta-quality-head">
                <SubChip name={String(p.subcontractor)} toneFor={toneFor} />
                <span className="spacer" />
                <span className="ta-quality-label">Preliminaries</span>
              </div>
              <div className="ta-prelims-amount">
                {formatCell(p.total_prelims)}
              </div>
              <ul className="ta-bullets">
                {asArray(p.key_items).map((item, j) => (
                  <li key={j}>{formatCell(item)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

PriceAnalysis.propTypes = {
  data: PropTypes.object.isRequired,
  taxTerm: PropTypes.string.isRequired,
  toneFor: PropTypes.func.isRequired,
};

// ---- programme -----------------------------------------------------------------

const ProgrammeAnalysis = ({ data, toneFor }) => {
  const leadIns = asArray(data.lead_in_periods);
  const durations = asArray(data.overall_duration);
  const siteVisits = asArray(data.site_visits);

  // One card per subcontractor, merging lead-in and duration entries.
  const names = [
    ...new Set(
      [...leadIns, ...durations].map((entry) => String(entry.subcontractor))
    ),
  ];

  return (
    <>
      <SectionHead
        title="Programme Analysis"
        subtitle="Lead-in, duration on site and resource levels compared"
      />

      {names.length > 0 && (
        <div className="ta-grid-2">
          {names.map((name) => {
            const lead = leadIns.find(
              (l) => normalizeName(l.subcontractor) === normalizeName(name)
            );
            const duration = durations.find(
              (d) => normalizeName(d.subcontractor) === normalizeName(name)
            );
            const notes = [
              lead?.notes,
              duration?.start_constraints,
              duration?.end_constraints,
              ...asArray(lead?.key_activities),
            ].filter(Boolean);

            return (
              <div className="ta-card" key={name}>
                <div className="ta-prog-head">
                  <SubChip name={name} toneFor={toneFor} />
                </div>
                <div className="ta-prog-tiles">
                  <div className="ta-prog-tile">
                    <div className="ta-stat-label">Lead-in</div>
                    <div className="ta-stat-value">
                      {formatCell(lead?.duration)}
                    </div>
                  </div>
                  <div className="ta-prog-tile">
                    <div className="ta-stat-label">Total weeks</div>
                    <div className="ta-stat-value">
                      {formatCell(duration?.total_weeks)}
                    </div>
                  </div>
                  <div className="ta-prog-tile">
                    <div className="ta-stat-label">Resources</div>
                    <div className="ta-stat-value">
                      {formatCell(duration?.resource_levels)}
                    </div>
                  </div>
                  <div className="ta-prog-tile">
                    <div className="ta-stat-label">Constraints</div>
                    <div className="ta-stat-value">
                      {formatCell(duration?.start_constraints) !== '—' ||
                      formatCell(duration?.end_constraints) !== '—'
                        ? 'See notes'
                        : '—'}
                    </div>
                  </div>
                </div>
                {notes.length > 0 && (
                  <ul className="ta-bullets">
                    {notes.map((note, i) => (
                      <li key={i}>{formatCell(note)}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {siteVisits.length > 0 && (
        <div className="ta-card">
          <div className="ta-card-title">Site visits</div>
          <DataTable
            columns={[
              {
                key: 'subcontractor',
                label: 'Subcontractor',
                width: '18%',
                render: (row) => (
                  <SubChip name={String(row.subcontractor)} toneFor={toneFor} />
                ),
              },
              {
                key: 'number_of_visits',
                label: 'Visits',
                width: '22%',
                strong: true,
              },
              // "Time or 'Not specified'" per the prompt — same open-ended
              // instruction as number_of_visits, so it gets the same 15% floor
              // rather than a width that assumes a short value.
              { key: 'duration', label: 'Duration', width: '15%' },
              { key: 'purpose', label: 'Purpose', width: '22%' },
              {
                key: 'cost_implications',
                label: 'Cost implications',
                width: '23%',
              },
            ]}
            rows={siteVisits}
          />
        </div>
      )}
    </>
  );
};

ProgrammeAnalysis.propTypes = {
  data: PropTypes.object.isRequired,
  toneFor: PropTypes.func.isRequired,
};

// ---- inclusions & exclusions -----------------------------------------------------

const coverageChip = (item, name) => {
  const target = normalizeName(name);
  // A collective entry ("Both"/"All") applies to every bidder — it must
  // light up every column, not silently match none of them.
  const applies = (list) =>
    asArray(list).some((n) => {
      const entry = normalizeName(n);
      return isCollectiveName(entry) || sameEntity(entry, target);
    });
  const included = applies(item.included_by);
  const excluded = applies(item.excluded_by);
  if (included && excluded) {
    return { tone: 'warning', label: 'Partial', Icon: MinusCircle };
  }
  if (included) {
    return { tone: 'success', label: 'Included', Icon: CheckCircle2 };
  }
  if (excluded) {
    return { tone: 'danger', label: 'Excluded', Icon: XCircle };
  }
  return { tone: 'neutral', label: 'Silent', Icon: MinusCircle };
};

const InclusionsExclusions = ({ data, subcontractorNames }) => {
  const scopeDifferences = asArray(data.scope_differences);
  const commercialTerms = asArray(data.commercial_terms);

  // Column per known subcontractor; fall back to the names the model used.
  const names = subcontractorNames.length
    ? subcontractorNames
    : [
        ...new Set(
          scopeDifferences.flatMap((item) => [
            ...asArray(item.included_by),
            ...asArray(item.excluded_by),
          ])
        ),
      ];

  // The header and every data row are separate grids, so any content-sized
  // track (`auto`) resolves per row: a long subcontractor name sizes its
  // header column to the name while the row below sizes the same column to a
  // chip. The columns then disagree and no amount of per-cell centring lines
  // the chips up under their names. Fixed 1fr subcontractor tracks resolve
  // identically in every row, so the grids share one axis.
  const gridTemplate = {
    gridTemplateColumns: `minmax(160px, 1.1fr) repeat(${names.length}, minmax(120px, 1fr)) minmax(180px, 1.5fr)`,
  };

  return (
    <>
      <SectionHead
        title="Inclusions & Exclusions"
        subtitle={`${scopeDifferences.length} scope items and ${commercialTerms.length} commercial terms compared side by side`}
      />

      {scopeDifferences.length > 0 && (
        <div className="ta-card">
          <div className="ta-card-title">Scope matrix</div>
          <div className="ta-matrix">
            <div className="ta-matrix-head" style={gridTemplate}>
              <span>Item</span>
              {names.map((name) => (
                <span key={name} className="ta-matrix-cell">
                  {name}
                </span>
              ))}
              <span>Why it matters</span>
            </div>
            {scopeDifferences.map((item, i) => (
              <div className="ta-matrix-row" style={gridTemplate} key={i}>
                <span className="ta-matrix-item">{formatCell(item.item)}</span>
                {names.map((name) => {
                  const { tone, label, Icon } = coverageChip(item, name);
                  return (
                    <span key={name} className="ta-matrix-cell">
                      <span className={`ta-chip tone-${tone}`}>
                        <Icon size={13} />
                        {label}
                      </span>
                    </span>
                  );
                })}
                <span className="ta-matrix-impact">
                  {formatCell(item.impact)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {commercialTerms.length > 0 && (
        <div className="ta-card">
          <div className="ta-card-title">Commercial terms</div>
          <DataTable
            columns={[
              { key: 'term', label: 'Term', width: '24%', strong: true },
              { key: 'variations', label: 'Variations', width: '38%' },
              { key: 'impact', label: 'Impact', width: '38%' },
            ]}
            rows={commercialTerms}
          />
        </div>
      )}
    </>
  );
};

InclusionsExclusions.propTypes = {
  data: PropTypes.object.isRequired,
  subcontractorNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// ---- risks -----------------------------------------------------------------------

const Risks = ({ data, toneFor }) => {
  const risks = asArray(data.risks);
  const [filter, setFilter] = useState('All');
  const names = [...new Set(risks.map((r) => String(r.subcontractor)))];
  const filtered =
    filter === 'All'
      ? risks
      : risks.filter((r) => String(r.subcontractor) === filter);

  return (
    <>
      <SectionHead
        title="Risks"
        subtitle={`${risks.length} risks — each with a recommended action`}
      >
        {names.length > 1 && (
          <FilterChips
            options={['All', ...names]}
            active={filter}
            onChange={setFilter}
          />
        )}
      </SectionHead>

      <div className="ta-risk-cards">
        {filtered.map((risk, i) => (
          <div className="ta-card" key={i}>
            <div className="ta-chip-row">
              <span className="ta-chip tone-neutral">
                {formatCell(risk.risk_category)}
              </span>
              <SubChip name={String(risk.subcontractor)} toneFor={toneFor} />
            </div>
            <div className="ta-item-body">{formatCell(risk.description)}</div>
            {risk.potential_impact && (
              <div className="ta-item-body">
                <b>Impact:</b> {formatCell(risk.potential_impact)}
              </div>
            )}
            {risk.mitigation_suggestion && (
              <div className="ta-action-box">
                <ArrowRight size={16} />
                <span>
                  <b>Action:</b> {formatCell(risk.mitigation_suggestion)}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

Risks.propTypes = {
  data: PropTypes.object.isRequired,
  toneFor: PropTypes.func.isRequired,
};

// ---- opportunities ------------------------------------------------------------------

const Opportunities = ({ data, toneFor }) => {
  const opportunities = asArray(data.opportunities);

  return (
    <>
      <SectionHead
        title="Opportunities"
        subtitle={`${opportunities.length} levers for savings, certainty or leverage`}
      />
      <div className="ta-grid-2">
        {opportunities.map((opp, i) => (
          <div className="ta-card" key={i}>
            <div className="ta-chip-row">
              <span className="ta-chip tone-neutral">
                {formatCell(opp.category)}
              </span>
              <span className="spacer" />
              <SubChip name={String(opp.subcontractor)} toneFor={toneFor} />
            </div>
            <div className="ta-item-body">{formatCell(opp.description)}</div>
            {opp.potential_benefit && (
              <div className="ta-action-box">
                <Lightbulb size={16} />
                <span>
                  <b>Benefit:</b> {formatCell(opp.potential_benefit)}
                </span>
              </div>
            )}
            {opp.implementation_notes && (
              <div className="ta-item-body" style={{ marginTop: 10 }}>
                <b>Implementation:</b> {formatCell(opp.implementation_notes)}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

Opportunities.propTypes = {
  data: PropTypes.object.isRequired,
  toneFor: PropTypes.func.isRequired,
};

// ---- clarifications ------------------------------------------------------------------

const Clarifications = ({ data, toneFor }) => {
  const clarifications = asArray(data.clarifications_required);
  const [filter, setFilter] = useState('All');
  const names = [
    ...new Set(clarifications.map((c) => String(c.subcontractor))),
  ];
  const filtered =
    filter === 'All'
      ? clarifications
      : clarifications.filter((c) => String(c.subcontractor) === filter);

  return (
    <>
      <SectionHead
        title="Clarifications Required"
        subtitle="A ready-made RFI list — issue these before any award recommendation"
      >
        {names.length > 1 && (
          <FilterChips
            options={['All', ...names]}
            active={filter}
            onChange={setFilter}
          />
        )}
      </SectionHead>

      <div className="ta-clar-cards">
        {filtered.map((clar, i) => (
          <div className="ta-clar-card" key={i}>
            <span className="ta-clar-n">
              {clarifications.indexOf(clar) + 1}
            </span>
            <div className="ta-clar-main">
              <div className="ta-chip-row">
                <span className="ta-item-title">{formatCell(clar.query)}</span>
                <span className="ta-chip tone-neutral">
                  {formatCell(clar.category)}
                </span>
                <SubChip name={String(clar.subcontractor)} toneFor={toneFor} />
              </div>
              <div className="ta-item-body">{formatCell(clar.reason)}</div>
              {clar.impact && (
                <div className="ta-item-body">
                  <b style={{ color: 'var(--danger-fg)' }}>If unresolved:</b>{' '}
                  {formatCell(clar.impact)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

Clarifications.propTypes = {
  data: PropTypes.object.isRequired,
  toneFor: PropTypes.func.isRequired,
};

// ---- root ------------------------------------------------------------------------

const TenderAnalysisResults = ({
  tenderData,
  status,
  taxTerm = 'Tax',
  subcontractors = [],
  boqFileName = null,
}) => {
  const [section, setSection] = useState('overview');
  const inProgress = !['SUCCESS', 'FAILURE', 'UNPROCESSABLE'].includes(status);

  // The canonical bidder list comes from the analysis payload itself — the
  // model quotes company names from the documents, which may bear no
  // resemblance to the labels typed at upload ("SubA" vs "in2clean"), so
  // upload names are only a fallback while nothing has streamed in yet.
  // Collection follows pipeline-completion order (price_analysis first),
  // so slots stay stable while later sections stream in; name variants
  // ("CPS" / "CPS Cleaning") fold into one slot via sameEntity.
  const bidders = useMemo(() => {
    const list = [];
    const add = (name) => {
      const display = String(name || '').trim();
      const norm = normalizeName(display);
      if (!norm || isNonBidderName(norm)) return;
      if (!list.some((b) => sameEntity(b.norm, norm))) {
        list.push({ norm, display });
      }
    };
    NAME_COLLECTION_ORDER.forEach((key) => {
      const data = tenderData?.[key];
      if (!data) return;
      Object.values(data).forEach((value) =>
        asArray(value).forEach((entry) => {
          if (entry && typeof entry === 'object') add(entry.subcontractor);
        })
      );
    });
    if (list.length === 0) {
      asArray(subcontractors).forEach((s) => add(s.name));
    }
    return list;
  }, [subcontractors, tenderData]);

  const toneFor = (name) => {
    const n = normalizeName(name);
    if (isCollectiveName(n)) return 'neutral';
    const index = bidders.findIndex((b) => sameEntity(b.norm, n));
    return index === -1 ? 'neutral' : SUB_TONES[index % SUB_TONES.length];
  };

  const subcontractorNames = bidders.map((b) => b.display);

  const counts = useMemo(() => {
    const result = {};
    SECTIONS.forEach(({ key, count }) => {
      result[key] = count(tenderData?.[key]);
    });
    return result;
  }, [tenderData]);

  const navigate = (key) => {
    setSection(key);
    const scroller = document.scrollingElement;
    if (scroller) scroller.scrollTop = 0;
  };

  const renderSectionContent = () => {
    if (section === 'overview') {
      return (
        <Overview
          tenderData={tenderData}
          counts={counts}
          inProgress={inProgress}
          toneFor={toneFor}
          onNavigate={navigate}
        />
      );
    }

    const config = SECTIONS.find((s) => s.key === section);
    const data = tenderData?.[section];

    if (!data) {
      return (
        <>
          <SectionHead title={config.title} />
          {inProgress ? (
            <div className="ta-card ta-pending">
              <CircularProgress size={16} thickness={4} />
              <span>This section is still being analysed…</span>
            </div>
          ) : (
            <div className="ta-card ta-empty">
              No data available for this section.
            </div>
          )}
        </>
      );
    }

    switch (section) {
      case 'executive_summary':
        return (
          <ExecutiveSummary data={data} taxTerm={taxTerm} toneFor={toneFor} />
        );
      case 'price_analysis':
        return (
          <PriceAnalysis data={data} taxTerm={taxTerm} toneFor={toneFor} />
        );
      case 'programme_analysis':
        return <ProgrammeAnalysis data={data} toneFor={toneFor} />;
      case 'inclusions_exclusions':
        return (
          <InclusionsExclusions
            data={data}
            subcontractorNames={subcontractorNames}
          />
        );
      case 'risks':
        return <Risks data={data} toneFor={toneFor} />;
      case 'opportunities':
        return <Opportunities data={data} toneFor={toneFor} />;
      case 'clarifications_required':
        return <Clarifications data={data} toneFor={toneFor} />;
      default:
        return null;
    }
  };

  return (
    <div className="ta-root">
      <nav className="ta-rail" aria-label="Tender analysis sections">
        <button
          type="button"
          className={`ta-rail-item ${section === 'overview' ? 'active' : ''}`}
          onClick={() => navigate('overview')}
        >
          <LayoutGrid size={18} className="ta-rail-icon" />
          <span className="ta-rail-title">Overview</span>
        </button>
        {SECTIONS.map(({ key, title, Icon }) => {
          const ready = Boolean(tenderData?.[key]);
          return (
            <button
              key={key}
              type="button"
              className={`ta-rail-item ${section === key ? 'active' : ''}`}
              onClick={() => navigate(key)}
            >
              <Icon size={18} className="ta-rail-icon" />
              <span className="ta-rail-title">{title}</span>
              {!ready && inProgress ? (
                <CircularProgress size={12} thickness={5} />
              ) : (
                <span className="ta-rail-count">
                  {counts[key] > 0 ? counts[key] : ''}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="ta-content">
        <PricingBasis boqFileName={boqFileName} />
        {renderSectionContent()}
      </div>
    </div>
  );
};

TenderAnalysisResults.propTypes = {
  tenderData: PropTypes.object,
  status: PropTypes.string.isRequired,
  taxTerm: PropTypes.string,
  subcontractors: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      files: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  boqFileName: PropTypes.string,
};

export default TenderAnalysisResults;
